// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Rub the pearl gently against the edge of your front tooth. A real pearl feels faintly gritty, like fine sandpaper. A glass or plastic imitation feels perfectly smooth. It is a crude test, it is free, and it is right far more often than it is wrong.

Then do the other checks below, because the tooth test tells you whether a pearl is real. It does not tell you whether it is natural or cultured, and that difference is worth a great deal of money.

## Three categories, not two

**Natural pearls** formed in a wild mollusc with no human involvement. They are genuinely rare, essentially all antique, and the most valuable by a wide margin.

**Cultured pearls** are real pearls grown in a farmed mollusc after a technician inserted a nucleus. The nacre is real and the pearl is real. Almost every pearl sold today is cultured.

**Imitation pearls** are glass, plastic or shell beads coated in a pearlescent lacquer. Majorica and similar brands make good ones. They are not pearls.

So "is it real" usually means two different questions at once: is it a pearl at all, and if so, is it natural or cultured.

## The checks you can do at home

| Check | Real pearl | Imitation |
| --- | --- | --- |
| Tooth test | Slightly gritty | Smooth, or faintly plastic |
| Surface under a loupe | Fine ridged texture, like a fingerprint | Even, or tiny pits and a paint line |
| The drill hole | Sharp, clean edge; nacre layers may show | Chipped, or lacquer flaking around it |
| Temperature | Cool for a moment against the skin, then warms | Plastic feels room temperature immediately |
| Weight | Noticeably heavy for its size | Plastic is light; glass can be heavy |
| Two rubbed together | Slight resistance and a whisper of grit | Slide smoothly and silently |
| Shape and matching | Small irregularities, never identical | Often flawlessly matched and perfectly round |

The single most reliable home check is the drill hole under magnification. Imitations are coated after drilling, so the coating tends to bridge or flake at the hole. On a real pearl you sometimes see the nacre in layers, and on a cultured pearl you may even see the boundary between nacre and the bead nucleus.

## Natural or cultured, and why it matters

This is the distinction that changes the price by a factor of ten or more, and it is the one you cannot settle at home. A natural pearl has nacre all the way through, growing outward from a tiny irritant. A cultured pearl has a round shell bead at its heart with a nacre layer over it, and that layer can be thin.

The reliable test is an X-ray, which shows the internal structure. Gemmological laboratories do this routinely. If a seller is describing pearls as natural and the price reflects that, a laboratory report is not optional.

Two practical signals in the meantime:

- **Age.** Cultured pearls became commercially available in the 1920s. A pearl necklace that is genuinely Victorian or Georgian is far more likely to contain natural pearls, and often small seed pearls.
- **Irregularity.** Natural pearls vary in shape and size within a strand, because nobody chose the nucleus. A perfectly graduated, perfectly round antique strand deserves a question.

## Nacre thickness, the thing nobody asks about

On cultured pearls, the value is largely in how thick the nacre is. A thin skinned pearl looks fine on day one and goes chalky or blisters within a few years of wear.

Look at the pearl against a strong light while rolling it slowly. If you can see a faint banding or a slightly different sheen where the bead sits underneath, the nacre is thin. A good pearl has depth, an inner glow rather than a surface shine.

## The words in a listing

- **Akoya**: the classic round white cultured pearl, mostly Japanese, usually 6 to 9 mm.
- **Freshwater**: grown in mussels, often without a bead nucleus, so frequently solid nacre. Enormous range of quality.
- **South Sea**: large, 9 to 20 mm, white to gold, expensive.
- **Tahitian**: naturally dark, grey through green to aubergine. Dyed black pearls are not Tahitian.
- **Baroque**: irregular in shape by nature, not a defect.
- **Seed pearls**: tiny naturals, common in Victorian work, often sewn onto mother of pearl backings.
- **Mabe or blister**: grown against the shell, so flat backed and hollow. Cheaper, and fragile.
- **Shell pearl** or **mother of pearl bead**: made from ground shell. An imitation, however lovely.

Treat the word **natural** carefully. Some sellers use it to mean "not dyed" rather than "not cultured".

## Dyed, treated and bleached

Most white pearls on the market have been bleached, which is accepted and stable. Dyeing is also common and should be disclosed.

To spot dye, look at the drill hole. Dye concentrates there and you often see a darker ring or colour pooled in the channel. On a strand, look where the pearls touch the knots: dye rubs.

## Caring for pearls, briefly

Pearls are soft, porous and organic. Perfume, hairspray and sun cream damage nacre, so pearls go on last and come off first. Wipe them with a barely damp soft cloth after wearing and let them dry flat.

Never put pearls in an ultrasonic cleaner and never soak a strand, because water travels into the drill holes and rots the silk from the inside. Restring every year or two with knots between the pearls. Our [guide to cleaning antique jewelry](/blog/how-to-clean-antique-jewelry) covers what else must stay out of water.

## Buying pearls second hand

Ask for a photograph of a drill hole under magnification, and a photograph of the strand laid flat in daylight rather than on a dark cloth under a spotlight. Dark backgrounds and hard light flatter pearls enormously.

Ask directly: are these natural or cultured, and how do you know. "Inherited, believed 1920s, not laboratory tested" is an honest answer and a fair basis for a price. Certainty without a report is the answer to be wary of.

On Nextmine the buyer's payment is held until the piece has arrived and been confirmed, so a strand that turns out not to match its description is recoverable rather than a lost bank transfer. [Browse what is listed](/s), or read [how payments work](/how-payments-work).
`;

export default body;
